import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, TrendingUp, ShieldCheck, Users, Heart, Award,
  Target, Globe, Zap, CheckCircle, BarChart3, Quote
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const SIGNALS = [
  { value: "81%", label: "of consumers must trust a brand before they buy", source: "Edelman Trust Barometer" },
  { value: "87%", label: "pay more for brands they trust", source: "Edelman" },
  { value: "70%", label: "of Gen Z prefer values-aligned organisations", source: "Exploding Topics 2025" },
  { value: "73%", label: "of Millennials choose purpose-driven employers", source: "Deloitte Global Insights" },
];

const PROBLEMS = [
  { icon: Quote, title: "Purpose without proof", desc: "Mission statements are everywhere, but stakeholders increasingly want evidence — not promises." },
  { icon: BarChart3, title: "Fragmented standards", desc: "ESG, B-Corp, ISO and DEI frameworks each tackle a slice. Leaders need one coherent operating system." },
  { icon: Users, title: "Trust deficit", desc: "Generational shifts are rewriting what 'good business' means. Consumers, talent and capital are all repricing trust." },
];

const PILLARS_DIFFERENTIATORS = [
  { icon: Target, title: "Six interlocking pillars", desc: "Conscious Leadership, Human-Centric Culture, Products & Services, Customers & Clients, Brand Vision, and Community Impact — measured together, not in silos." },
  { icon: ShieldCheck, title: "Independent verification", desc: "Third-party evaluators score every assessment using calibrated rubrics. The badge means something because it is earned." },
  { icon: TrendingUp, title: "Built for growth", desc: "Every certification surfaces priority improvements, so the journey doubles as a strategic operating roadmap." },
  { icon: Globe, title: "Globally portable", desc: "One assessment, recognised across markets. Speak the same language of trust to every stakeholder." },
];

const TESTIMONIALS = [
  { quote: "CNESS gave our values a structure. We don't just say we care anymore — we can prove it across six dimensions.", name: "Group CEO", role: "Mid-cap manufacturing, India" },
  { quote: "The certification report became our 18-month transformation roadmap. Best ROI we've had on any audit.", name: "Chief People Officer", role: "Enterprise SaaS" },
  { quote: "Customers ask for ESG numbers. Investors ask for governance. Talent asks for culture. CNESS answers all three with one badge.", name: "Founder", role: "Series-B fintech" },
];

const WhyCNESSPage = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC] overflow-hidden" data-testid="why-cness-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">Why CNESS</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] mb-6 leading-[1.1]">
              Trust is the <span className="text-[#FDB813]">new currency</span><br />
              of business.
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Stakeholders don't reward intentions anymore — they reward evidence. CNESS exists to turn the
              responsible practices already inside your organisation into a credible, communicable signal of trust.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-3">The numbers behind the shift</h2>
            <p className="text-gray-600">Why responsible practice now drives revenue, talent and capital.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {SIGNALS.map((s, i) => (
              <div key={i} className="bg-gradient-to-br from-[#FEF3C7] to-white rounded-2xl p-6 border border-[#FDB813]/20">
                <div className="text-4xl font-bold text-[#FDB813] mb-2">{s.value}</div>
                <p className="text-sm text-gray-700 leading-snug">{s.label}</p>
                <p className="text-xs text-gray-400 mt-3">— {s.source}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">The gap conscious leaders feel every day</h2>
            <p className="text-lg text-gray-600">Three forces are reshaping the contract between business and society.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PROBLEMS.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-[#FEF3C7] rounded-xl flex items-center justify-center mb-5">
                  <p.icon className="w-6 h-6 text-[#FDB813]" />
                </div>
                <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">{p.title}</h3>
                <p className="text-gray-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What CNESS does differently */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-6">
                What CNESS does <span className="text-[#FDB813]">differently</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                CNESS is not a checklist. It is a framework that translates responsible business into a measurable,
                continuously-improving operating system — verified by independent evaluators and recognised globally.
              </p>
              <Button
                onClick={() => navigate("/certification")}
                data-testid="why-cness-cta-cert"
                className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-6 rounded-xl"
              >
                See How Certification Works <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <div className="space-y-4">
              {PILLARS_DIFFERENTIATORS.map((d, i) => (
                <div key={i} className="flex items-start gap-4 bg-[#F8FAFC] rounded-2xl p-5 hover:shadow-md transition">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <d.icon className="w-6 h-6 text-[#FDB813]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1E3A5F] mb-1">{d.title}</h4>
                    <p className="text-sm text-gray-600">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes Strip */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A5F] to-[#0F172A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Outcomes our champions report</h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Certified organisations consistently outperform on the metrics that compound over time.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, label: "Customer loyalty", value: "+34%" },
              { icon: Users, label: "Talent retention", value: "+28%" },
              { icon: Zap, label: "Operational efficiency", value: "+19%" },
              { icon: Award, label: "Brand premium", value: "+22%" },
            ].map((o, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <o.icon className="w-8 h-8 text-[#FDB813] mb-4" />
                <div className="text-3xl font-bold text-[#FDB813] mb-1">{o.value}</div>
                <p className="text-sm text-white/70">{o.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">Voices from the network</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-2xl p-8 relative">
                <Quote className="w-8 h-8 text-[#FDB813]/30 absolute top-6 right-6" />
                <p className="text-gray-700 leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#FEF3C7] to-[#FDB813]/30 rounded-full flex items-center justify-center font-bold text-[#1E3A5F]">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-[#1E3A5F] text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CheckCircle className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">Ready to make your trust visible?</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Begin with a free discovery conversation, or jump straight into the certification journey that fits your stage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/enquiries")}
              data-testid="why-cness-cta-enquire"
              className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-6 rounded-xl"
            >
              Start Your Enquiry <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate("/contact")}
              variant="outline"
              data-testid="why-cness-cta-contact"
              className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white px-8 py-6 rounded-xl"
            >
              Talk to Our Team
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default WhyCNESSPage;
